import { notify } from '@affine/component';
import {
  AuthContainer,
  AuthContent,
  AuthHeader,
} from '@affine/component/auth-components';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import type { AuthSessionStatus } from '@affine/core/modules/cloud/entities/session';
import { Unreachable } from '@affine/env/constant';
import {
  type Dispatch,
  type KeyboardEvent,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import type { SignInState } from '.';
import { Back } from './back';
import { clerkFapiUrl, getCachedDevBrowserJwt } from './clerk-dev-browser';

type ClerkError = { errors: Array<{ message: string }> };

async function clerkSignInCreate(email: string): Promise<string> {
  const url = await clerkFapiUrl('/v1/client/sign_ins');
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      identifier: email,
      strategy: 'email_code',
    }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(
      (data as ClerkError).errors?.[0]?.message ?? 'Falha ao reenviar o código'
    );
  }
  return (data as { response: { id: string } }).response.id;
}

async function clerkVerifyCode(
  signInId: string,
  code: string
): Promise<boolean> {
  const url = await clerkFapiUrl(
    `/v1/client/sign_ins/${signInId}/attempt_first_factor`
  );
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ strategy: 'email_code', code }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(
      (data as ClerkError).errors?.[0]?.message ?? 'Código inválido'
    );
  }
  return (
    (data as { response: { status: string } }).response.status === 'complete'
  );
}

// ─── 6-slot OTP input — design system spec ────────────────────────────────────

const OtpInput = ({
  value,
  onChange,
  onComplete,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onComplete: (completedValue: string) => void;
  disabled: boolean;
}) => {
  const refs = useRef<Array<HTMLInputElement | null>>(Array(6).fill(null));
  const digits = value.padEnd(6, ' ').slice(0, 6).split('');

  const handleKeyDown = useCallback(
    (index: number, e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        e.preventDefault();
        onChange(value.slice(0, Math.max(index, 0)));
        if (index > 0) refs.current[index - 1]?.focus();
      } else if (e.key === 'ArrowLeft' && index > 0) {
        refs.current[index - 1]?.focus();
      } else if (e.key === 'ArrowRight' && index < 5) {
        refs.current[index + 1]?.focus();
      }
    },
    [onChange, value]
  );

  const handleInput = useCallback(
    (index: number, raw: string) => {
      const digit = raw.replace(/\D/g, '').slice(-1);
      if (!digit) return;
      const arr = value.padEnd(6, ' ').split('');
      arr[index] = digit;
      const newVal = arr.join('').trimEnd();
      onChange(newVal);
      if (index < 5) {
        refs.current[index + 1]?.focus();
      } else if (newVal.replace(/ /g, '').length === 6) {
        onComplete(newVal);
      }
    },
    [onChange, onComplete, value]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pasted = e.clipboardData
        .getData('text')
        .replace(/\D/g, '')
        .slice(0, 6);
      if (!pasted) return;
      onChange(pasted);
      refs.current[Math.min(pasted.length, 5)]?.focus();
      if (pasted.length === 6) onComplete(pasted);
    },
    [onChange, onComplete]
  );

  return (
    <div
      style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        marginBottom: '40px',
      }}
    >
      {digits.map((d, i) => (
        <input
          key={i}
          ref={el => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d.trim()}
          disabled={disabled}
          onChange={e => handleInput(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={e => {
            e.currentTarget.select();
            e.currentTarget.style.borderColor = 'rgba(202, 249, 1, 0.5)';
          }}
          onBlur={e => {
            e.currentTarget.style.borderColor = 'transparent';
          }}
          style={{
            width: '52px',
            height: '64px',
            borderRadius: '8px',
            border: '1px solid transparent',
            backgroundColor: '#3C3C3C',
            color: '#FFFFFF',
            fontSize: '24px',
            fontWeight: 500,
            textAlign: 'center',
            outline: 'none',
            caretColor: '#CAF901',
            transition: 'border-color 0.15s',
            cursor: disabled ? 'not-allowed' : 'text',
            opacity: disabled ? 0.5 : 1,
          }}
        />
      ))}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const SignInWithEmailStep = ({
  state,
  changeState,
  onAuthenticated: _onAuthenticated,
}: {
  state: SignInState;
  changeState: Dispatch<SetStateAction<SignInState>>;
  onAuthenticated?: (status: AuthSessionStatus) => void;
}) => {
  const email = state.email;
  const clerkSignInId = state.clerkSignInId;

  if (!email || !clerkSignInId) {
    throw new Unreachable();
  }

  // 60s countdown starts on mount (code was just sent by the previous step)
  const [resendCountDown, setResendCountDown] = useState(60);
  useEffect(() => {
    const timer = setInterval(
      () => setResendCountDown(c => Math.max(c - 1, 0)),
      1000
    );
    return () => clearInterval(timer);
  }, []);

  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | undefined>();

  const onOtpChanged = useCallback((value: string) => {
    setOtp(value);
    setOtpError(undefined);
  }, []);

  // ── Verify OTP via Clerk FAPI, then complete AFFiNE session via OIDC ──────
  const onContinue = useAsyncCallback(
    async (otpOverride?: string) => {
      if (isVerifying) return;
      const code = otpOverride ?? otp;
      if (code.length !== 6 || !/^\d{6}$/.test(code)) {
        setOtpError('Código inválido. Verifique e tente novamente.');
        return;
      }

      setIsVerifying(true);
      try {
        const completed = await clerkVerifyCode(clerkSignInId, code);
        if (completed) {
          // Clerk session is now established — use OIDC to get an AFFiNE session
          const params = new URLSearchParams({ provider: 'OIDC' });
          if (state.redirectUrl) params.set('redirect_uri', state.redirectUrl);
          const dbJwt = getCachedDevBrowserJwt();
          if (dbJwt) params.set('__clerk_db_jwt', dbJwt);
          window.location.href = `/oauth/login?${params.toString()}`;
        }
      } catch (err: unknown) {
        setOtpError((err as Error).message);
      } finally {
        setIsVerifying(false);
      }
    },
    [clerkSignInId, isVerifying, otp, state.redirectUrl]
  );

  // ── Resend: create a new Clerk sign-in for the same email ─────────────────
  const onResend = useAsyncCallback(async () => {
    if (isSending || resendCountDown > 0) return;
    setIsSending(true);
    try {
      const newSignInId = await clerkSignInCreate(email);
      setResendCountDown(60);
      setOtp('');
      setOtpError(undefined);
      changeState(prev => ({ ...prev, clerkSignInId: newSignInId }));
    } catch (err: unknown) {
      notify.error({
        title: 'Falha ao reenviar',
        message: (err as Error).message,
      });
    } finally {
      setIsSending(false);
    }
  }, [changeState, email, isSending, resendCountDown]);

  const canResend = resendCountDown <= 0 && !isSending;

  return (
    <AuthContainer>
      <AuthHeader title="Confira seu e-mail" />

      <AuthContent>
        {/* Subtitle */}
        <p
          style={{
            fontSize: '14px',
            fontWeight: 400,
            color: '#8F8F8F',
            textAlign: 'center',
            marginBottom: '40px',
            lineHeight: '1.5',
          }}
        >
          Enviamos um código de verificação para{' '}
          <span style={{ color: '#CAF901', fontWeight: 500 }}>{email}</span>
        </p>

        {/* Error block */}
        {otpError && (
          <div
            style={{
              borderRadius: '8px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              padding: '12px',
              marginBottom: '24px',
              fontSize: '14px',
              color: '#f87171',
            }}
          >
            {otpError}
          </div>
        )}

        {/* 6-slot OTP */}
        <OtpInput
          value={otp}
          onChange={onOtpChanged}
          onComplete={onContinue}
          disabled={isVerifying}
        />

        {/* Confirm button */}
        <button
          data-testid="continue-code-button"
          onClick={onContinue}
          disabled={isVerifying || otp.length !== 6}
          style={{
            display: 'flex',
            height: '48px',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            backgroundColor:
              isVerifying || otp.length !== 6
                ? 'rgba(202,249,1,0.5)'
                : '#CAF901',
            border: 'none',
            fontSize: '16px',
            fontWeight: 400,
            color: '#242D00',
            cursor: isVerifying || otp.length !== 6 ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.15s',
            marginBottom: '40px',
          }}
          onMouseEnter={e => {
            if (!isVerifying && otp.length === 6)
              (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                'rgba(202,249,1,0.9)';
          }}
          onMouseLeave={e => {
            if (!isVerifying && otp.length === 6)
              (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                '#CAF901';
          }}
        >
          {isVerifying ? 'Verificando…' : 'Confirmar código'}
        </button>

        {/* Resend row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            fontSize: '14px',
            color: '#F9F9F9',
          }}
        >
          <span>Não recebeu o email?</span>
          {canResend ? (
            <button
              onClick={onResend}
              style={{
                background: 'none',
                border: 'none',
                color: '#CAF901',
                fontWeight: 500,
                fontSize: '14px',
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
              }}
            >
              Clique para reenviar
            </button>
          ) : (
            <>
              <span style={{ color: '#8F8F8F' }}>Espere para reenviar</span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '20px',
                  minWidth: '20px',
                  borderRadius: '4px',
                  backgroundColor: '#3C3C3C',
                  paddingInline: '4px',
                  fontSize: '10px',
                  color: '#CACACA',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {String(resendCountDown).padStart(2, '0')}
              </span>
            </>
          )}
        </div>
      </AuthContent>

      <Back changeState={changeState} />
    </AuthContainer>
  );
};
